import React from "react";
import PhotoUploadCard from "./PhotoUploadCard";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import Icon from "../../../components/AppIcon";

const FIXED_PANEL_IDS = [1, 2, 3, 4];
const FEET_TO_METERS = 0.3048;

const TechnicalWorkPhase = ({
  data,
  onUpdate,
  onComplete,
  customerId,
  uploadPhoto,
  isSaving = false,
  isCompleting = false,
  isVerifyingLocation = false,
  currentStep = 1,
  onStepChange,
}) => {
  const steps = [
    {
      number: 1,
      title: "Panel",
      icon: "Box",
    },
    {
      number: 2,
      title: "Inverter",
      icon: "Zap",
    },
    {
      number: 3,
      title: "Installation",
      icon: "Camera",
    },
    {
      number: 4,
      title: "Earthing",
      icon: "Anchor",
    },
    {
      number: 5,
      title: "Wiring",
      icon: "Cable",
    },
  ];

  const handlePanelUpdate = (panelId, field, value) => {
    const updatedPanels = {
      ...data?.panels,
      [panelId]: {
        ...data?.panels?.[panelId],
        [field]: value,
      },
    };
    onUpdate({
      ...data,
      panels: updatedPanels,
    });
  };

  const handleWiringInverterUpdate = (field, value) => {
    onUpdate({
      ...data,
      wiringInverter: {
        ...data?.wiringInverter,
        [field]: value,
      },
    });
  };

  const handleEarthingUpdate = (field, value) => {
    onUpdate({
      ...data,
      earthing: {
        ...data?.earthing,
        [field]: value,
      },
    });
  };

  const handleInstallationPhotoUpdate = (field, value) => {
    onUpdate({
      ...data,
      installationPhotos: {
        ...(data?.installationPhotos || {}),
        [field]: value,
      },
    });
  };

  const handleWiringUpdate = (field, value) => {
    const wiring = data?.wiring || {};
    const nextWiring = {
      ...wiring,
      [field]: value,
    };

    if (field === "dcRedFeet" || field === "dcBlackFeet") {
      const redFeet = Number.parseFloat(
        field === "dcRedFeet" ? value : wiring?.dcRedFeet,
      );
      const blackFeet = Number.parseFloat(
        field === "dcBlackFeet" ? value : wiring?.dcBlackFeet,
      );
      const redMeters = Number.isFinite(redFeet) ? redFeet * FEET_TO_METERS : null;
      const blackMeters = Number.isFinite(blackFeet)
        ? blackFeet * FEET_TO_METERS
        : null;
      nextWiring.dcMeters =
        redMeters != null || blackMeters != null
          ? Number((redMeters || 0) + (blackMeters || 0)).toFixed(2)
          : "";
    }

    if (field === "acFeet") {
      const acFeet = Number.parseFloat(value);
      nextWiring.acMeters = Number.isFinite(acFeet)
        ? Number(acFeet * FEET_TO_METERS).toFixed(2)
        : "";
    }

    if (field === "earthingFeet") {
      const earthingFeet = Number.parseFloat(value);
      nextWiring.earthingMeters = Number.isFinite(earthingFeet)
        ? Number(earthingFeet * FEET_TO_METERS).toFixed(2)
        : "";
    }

    onUpdate({
      ...data,
      wiring: nextWiring,
    });
  };

  const isPanelComplete = (panelId) => {
    const panel = data?.panels?.[panelId];
    return Boolean(panel?.serialNumberPhoto);
  };

  const isWiringInverterComplete = () => {
    const wiringInverter = data?.wiringInverter || {};
    const serialPhoto =
      wiringInverter?.inverterSerialPhoto ??
      wiringInverter?.inverter_serial_photo;
    return (
      String(
        wiringInverter?.inverterMake || wiringInverter?.inverter_make || "",
      ).trim() && Boolean(serialPhoto)
    );
  };

  const isInstallationPhotosComplete = () => {
    const ip = data?.installationPhotos || {};
    return Boolean(
      ip.panelSideWithCustomer &&
      ip.panelFrontWithCustomer &&
      ip.inverterInstallationPhoto,
    );
  };

  const isEarthingComplete = () => {
    const earthing = data?.earthing || {};
    return Boolean(earthing?.photo1);
  };

  const isWiringComplete = () => {
    const wiring = data?.wiring || {};
    const hasDcRed = String(wiring?.dcRedFeet || "").trim() !== "";
    const hasDcBlack = String(wiring?.dcBlackFeet || "").trim() !== "";
    const hasAc = String(wiring?.acFeet || "").trim() !== "";
    const hasEarthing = String(wiring?.earthingFeet || "").trim() !== "";
    return hasDcRed && hasDcBlack && hasAc && hasEarthing;
  };

  const allPanelsComplete = FIXED_PANEL_IDS.every((id) => isPanelComplete(id));

  const isStep1Complete = allPanelsComplete;
  const isStep2Complete = isWiringInverterComplete();
  const isStep3Complete = isInstallationPhotosComplete();
  const isStep4Complete = isEarthingComplete();
  const isStep5Complete = isWiringComplete();

  const handleNext = () => {
    if (currentStep < steps.length && onStepChange) {
      onStepChange(currentStep + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1 && onStepChange) {
      onStepChange(currentStep - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleStepClick = (stepNumber) => {
    if (isSaving || isCompleting || !onStepChange) return;
    if (stepNumber === currentStep) return;
    onStepChange(stepNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const installationPhotos = data?.installationPhotos || {};

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Step Progress Indicator - horizontal scroll on mobile */}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
          <h2 className="text-lg md:text-xl font-semibold text-foreground min-w-0">
            Technical Work - Step {currentStep} of {steps.length}
          </h2>
          <span className="text-sm text-muted-foreground flex-shrink-0">
            {Math.round((currentStep / steps.length) * 100)}% Complete
          </span>
        </div>
        <div className="overflow-x-auto -mx-1 px-1 pb-1 md:overflow-visible md:mx-0 md:px-0 md:pb-0">
          <div className="flex items-center flex-nowrap min-w-0 md:flex-wrap md:justify-between gap-2">
            {steps.map((step, index) => {
              const stepComplete =
                (step.number === 1 && isStep1Complete) ||
                (step.number === 2 && isStep2Complete) ||
                (step.number === 3 && isStep3Complete) ||
                (step.number === 4 && isStep4Complete) ||
                (step.number === 5 && isStep5Complete);
              const isActive = currentStep === step.number;

              return (
                <React.Fragment key={step.number}>
                  <button
                    type="button"
                    onClick={() => handleStepClick(step.number)}
                    disabled={isSaving || isCompleting}
                    className={`flex flex-col items-center flex-shrink-0 min-w-[72px] md:min-w-0 md:flex-1 rounded-lg px-1 py-1 -mx-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ${
                      isActive
                        ? "bg-primary/5"
                        : "hover:bg-muted/80 cursor-pointer"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all ${
                        isActive
                          ? "bg-primary border-primary text-primary-foreground"
                          : stepComplete
                            ? "bg-success border-success text-white"
                            : "bg-muted border-border text-muted-foreground"
                      }`}
                    >
                      {stepComplete ? (
                        <Icon name="Check" size={20} />
                      ) : (
                        <span className="text-sm font-semibold">
                          {step.number}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-xs mt-2 text-center whitespace-nowrap max-w-[100px] md:max-w-none leading-tight ${
                        isActive
                          ? "text-primary font-medium"
                          : "text-muted-foreground"
                      }`}
                    >
                      {step.title}
                    </span>
                  </button>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-shrink-0 w-3 md:flex-1 md:w-auto h-0.5 mx-1 md:mx-2 transition-all ${
                        stepComplete ? "bg-success" : "bg-border"
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
      {/* Step 1: Panel Installation (4 fixed slots) */}
      {currentStep === 1 && (
        <>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Info" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="text-sm md:text-base font-medium text-foreground mb-1">
                  Panel Serial Numbers
                </h4>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Upload a clear photo of the 4 panels serial number.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {FIXED_PANEL_IDS.map((panelId) => {
              const panel = data?.panels?.[panelId] || {};
              const isComplete = isPanelComplete(panelId);

              return (
                <div
                  key={panelId}
                  className="bg-card border border-border rounded-lg p-4 md:p-6"
                >
                  <div className="mb-4">
                    <h3 className="text-base md:text-lg font-semibold text-foreground flex flex-wrap items-center gap-2 min-w-0">
                      <span>Panel {panelId}</span>
                      {isComplete && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-success/10 text-success text-xs font-medium rounded flex-shrink-0">
                          <Icon name="Check" size={12} />
                          Complete
                        </span>
                      )}
                    </h3>
                  </div>
                  <div className="space-y-4">
                    <PhotoUploadCard
                      title={`Panel ${panelId} serial number photo`}
                      description="Photo of the panel nameplate showing the serial number"
                      photoUrl={panel?.serialNumberPhoto}
                      onUpload={(url) =>
                        handlePanelUpdate(panelId, "serialNumberPhoto", url)
                      }
                      onRemove={() =>
                        handlePanelUpdate(panelId, "serialNumberPhoto", null)
                      }
                      uploadFunction={
                        uploadPhoto && customerId
                          ? (file) =>
                              uploadPhoto(
                                customerId,
                                file,
                                `panel_${panelId}_serial`,
                              )
                          : null
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Step 2: Inverter */}
      {currentStep === 2 && (
        <>
          <div className="bg-card border border-border rounded-lg p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              Inverter
            </h3>
            <div className="space-y-4">
              <Input
                label="Inverter make"
                type="text"
                placeholder="e.g. brand / model"
                value={
                  data?.wiringInverter?.inverterMake ??
                  data?.wiringInverter?.inverter_make ??
                  ""
                }
                onChange={(e) =>
                  handleWiringInverterUpdate("inverterMake", e?.target?.value)
                }
              />
              <PhotoUploadCard
                title="Inverter serial number photo"
                description="Clear photo of the inverter nameplate / serial label"
                photoUrl={
                  data?.wiringInverter?.inverterSerialPhoto ??
                  data?.wiringInverter?.inverter_serial_photo
                }
                onUpload={(url) =>
                  handleWiringInverterUpdate("inverterSerialPhoto", url)
                }
                onRemove={() =>
                  handleWiringInverterUpdate("inverterSerialPhoto", null)
                }
                uploadFunction={
                  uploadPhoto && customerId
                    ? (file) =>
                        uploadPhoto(customerId, file, "inverter_serial_photo")
                    : null
                }
              />
            </div>
          </div>
        </>
      )}

      {/* Step 3: Installation — site photos */}
      {currentStep === 3 && (
        <>
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon name="Info" size={20} className="text-primary" />
              </div>
              <div>
                <h4 className="text-sm md:text-base font-medium text-foreground mb-1">
                  Installation photos
                </h4>
                <p className="text-xs md:text-sm text-muted-foreground">
                  Capture panel and structure with the customer where noted,
                  plus an inverter installation photo.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            <PhotoUploadCard
              title="Panel & Structure Side View (with customer)"
              description="Side view showing panel, structure, and customer"
              photoUrl={installationPhotos.panelSideWithCustomer}
              onUpload={(url) =>
                handleInstallationPhotoUpdate("panelSideWithCustomer", url)
              }
              onRemove={() =>
                handleInstallationPhotoUpdate("panelSideWithCustomer", null)
              }
              uploadFunction={
                uploadPhoto && customerId
                  ? (file) =>
                      uploadPhoto(
                        customerId,
                        file,
                        "installation_panel_side_customer",
                      )
                  : null
              }
            />
            <PhotoUploadCard
              title="Panel & Structure Front View (with customer)"
              description="Front view showing panel, structure, and customer"
              photoUrl={installationPhotos.panelFrontWithCustomer}
              onUpload={(url) =>
                handleInstallationPhotoUpdate("panelFrontWithCustomer", url)
              }
              onRemove={() =>
                handleInstallationPhotoUpdate("panelFrontWithCustomer", null)
              }
              uploadFunction={
                uploadPhoto && customerId
                  ? (file) =>
                      uploadPhoto(
                        customerId,
                        file,
                        "installation_panel_front_customer",
                      )
                  : null
              }
            />
            <PhotoUploadCard
              title="Inverter Photo (with customer)"
              description="Photo of the installed inverter on site with the customer"
              photoUrl={installationPhotos.inverterInstallationPhoto}
              onUpload={(url) =>
                handleInstallationPhotoUpdate("inverterInstallationPhoto", url)
              }
              onRemove={() =>
                handleInstallationPhotoUpdate("inverterInstallationPhoto", null)
              }
              uploadFunction={
                uploadPhoto && customerId
                  ? (file) =>
                      uploadPhoto(
                        customerId,
                        file,
                        "installation_inverter_site",
                      )
                  : null
              }
            />
          </div>
        </>
      )}

      {/* Step 4: Earthing */}
      {currentStep === 4 && (
        <>
          <div className="bg-card border border-border rounded-lg p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              Earthing
            </h3>
            <div className="max-w-xl">
              <PhotoUploadCard
                title="Earthing photo"
                description="Earthing installation photo"
                photoUrl={data?.earthing?.photo1}
                onUpload={(url) => handleEarthingUpdate("photo1", url)}
                onRemove={() => handleEarthingUpdate("photo1", null)}
                uploadFunction={
                  uploadPhoto && customerId
                    ? (file) => uploadPhoto(customerId, file, "earthing_1")
                    : null
                }
              />
            </div>
          </div>
        </>
      )}

      {/* Step 5: Wiring */}
      {currentStep === 5 && (
        <>
          <div className="bg-card border border-border rounded-lg p-4 md:p-6">
            <h3 className="text-base md:text-lg font-semibold text-foreground mb-4">
              Wiring
            </h3>
            <div className="space-y-6">
              <div className="space-y-4">
                <h4 className="text-sm md:text-base font-medium text-foreground">
                  DC Cable Length (4 sqmm)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Red (feet)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={data?.wiring?.dcRedFeet || ""}
                    onChange={(e) =>
                      handleWiringUpdate("dcRedFeet", e?.target?.value)
                    }
                  />
                  <Input
                    label="Black (feet)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={data?.wiring?.dcBlackFeet || ""}
                    onChange={(e) =>
                      handleWiringUpdate("dcBlackFeet", e?.target?.value)
                    }
                  />
                </div>
                <Input
                  label="Total (meters)"
                  type="text"
                  value={data?.wiring?.dcMeters || ""}
                  readOnly
                  placeholder="Auto calculated"
                />
              </div>

              <div className="space-y-4">
                <h4 className="text-sm md:text-base font-medium text-foreground">
                  AC Cable Length (2 Core, 4 sqmm)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Length (feet)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={data?.wiring?.acFeet || ""}
                    onChange={(e) =>
                      handleWiringUpdate("acFeet", e?.target?.value)
                    }
                  />
                  <Input
                    label="Length (meters)"
                    type="text"
                    value={data?.wiring?.acMeters || ""}
                    readOnly
                    placeholder="Auto calculated"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-sm md:text-base font-medium text-foreground">
                  Earthing Cable Length (16 sqmm)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    label="Length (feet)"
                    type="number"
                    min="0"
                    step="0.01"
                    value={data?.wiring?.earthingFeet || ""}
                    onChange={(e) =>
                      handleWiringUpdate("earthingFeet", e?.target?.value)
                    }
                  />
                  <Input
                    label="Length (meters)"
                    type="text"
                    value={data?.wiring?.earthingMeters || ""}
                    readOnly
                    placeholder="Auto calculated"
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Navigation Buttons - stack on mobile */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Button
          variant="outline"
          size="lg"
          iconName="ChevronLeft"
          iconPosition="left"
          onClick={handlePrevious}
          disabled={currentStep === 1 || isSaving || isCompleting}
          className="w-full sm:w-auto"
        >
          Previous
        </Button>

        <div className="flex flex-wrap gap-2 justify-center sm:justify-end">
          {currentStep < steps.length ? (
            <Button
              variant="default"
              size="lg"
              iconName="ChevronRight"
              iconPosition="right"
              onClick={handleNext}
              disabled={isSaving || isCompleting}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="success"
              size="lg"
              onClick={onComplete}
              disabled={isSaving || isCompleting}
              loading={isCompleting}
              iconName={isVerifyingLocation ? "MapPin" : "CheckCircle2"}
              iconPosition="left"
            >
              {isVerifyingLocation
                ? "Verifying Location..."
                : "Complete Technical Work"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicalWorkPhase;
